// Padrón de comensales por contrato (Fase 0 del modelo por persona). Alta/edición,
// importación masiva, y flag modo_servicio del contrato (bloque | comensal).
// Catering → Operación → Comensales.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Users, UserPlus, Upload, Pencil, Trash2, Check, X, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls } from '@/features/inventario/shared';
import { catRpc, invalidateCatering } from './shared';

interface ContratoRow { contrato_id: string; codigo: string; nombre: string; modo_servicio: 'bloque' | 'comensal'; comensales: number }
interface Comensal { id: string; codigo: string | null; nombre: string; partner_id: string | null; partner: string | null; restricciones: string[]; dieta_tipo_id: string | null; dieta: string | null; notas: string | null; activo: boolean }
interface Dieta { id: string; nombre: string; codigo: string }
type Editor = { id?: string; codigo: string; nombre: string; restricciones: string; dieta_tipo_id: string; notas: string; activo: boolean };

const nuevoEditor = (): Editor => ({ codigo: '', nombre: '', restricciones: '', dieta_tipo_id: '', notas: '', activo: true });

export function ComensalesCatering() {
  const qc = useQueryClient();
  const [contratoId, setContratoId] = useState('');
  const [busca, setBusca] = useState('');
  const [editor, setEditor] = useState<Editor | null>(null);
  const [importar, setImportar] = useState<string | null>(null);

  const { data: contratos = [] } = useQuery<ContratoRow[]>({
    queryKey: ['cat-comensal-contratos'],
    queryFn: () => catRpc<ContratoRow[]>('cat_comensal_contratos'),
  });
  const contrato = contratos.find((c) => c.contrato_id === contratoId) ?? contratos[0];
  const cid = contrato?.contrato_id ?? '';

  const { data: comensales = [], isLoading } = useQuery<Comensal[]>({
    queryKey: ['cat-comensales', cid, busca],
    queryFn: () => catRpc<Comensal[]>('cat_comensal_listar', { _contrato_id: cid, _busca: busca || null }),
    enabled: !!cid,
  });
  const { data: dietas = [] } = useQuery<Dieta[]>({ queryKey: ['cat-dietas-tipo'], queryFn: () => catRpc<Dieta[]>('cat_dieta_listar') });

  const invalidate = () => { invalidateCatering(qc); void qc.invalidateQueries({ queryKey: ['cat-comensal-contratos'] }); };

  const guardar = useMutation({
    mutationFn: (e: Editor) => catRpc('cat_comensal_guardar', {
      _contrato_id: cid, _nombre: e.nombre, _codigo: e.codigo || null, _id: e.id ?? null,
      _restricciones: e.restricciones.split(/[;,|]/).map((s) => s.trim()).filter(Boolean),
      _dieta_tipo_id: e.dieta_tipo_id || null, _notas: e.notas || null, _activo: e.activo,
    }),
    onSuccess: () => { invalidate(); setEditor(null); toast.success('Comensal guardado'); },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const baja = useMutation({
    mutationFn: (id: string) => catRpc('cat_comensal_baja', { _id: id }),
    onSuccess: () => { invalidate(); toast.success('Comensal dado de baja'); },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const modo = useMutation({
    mutationFn: (m: 'bloque' | 'comensal') => catRpc('cat_contrato_modo_set', { _contrato_id: cid, _modo: m }),
    onSuccess: () => { invalidate(); toast.success('Modo de servicio actualizado'); },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const importarMut = useMutation({
    mutationFn: (texto: string) => {
      const filas = texto.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
        const p = l.split(/[\t,]/).map((x) => x.trim());
        return { codigo: p[0] ?? '', nombre: p[1] ?? '', restricciones: p.slice(2).join(';') };
      });
      return catRpc<{ insertados: number; actualizados: number; omitidos: number }>('cat_comensal_importar', { _contrato_id: cid, _filas: filas });
    },
    onSuccess: (r) => { invalidate(); setImportar(null); toast.success(`Importados ${r.insertados} · actualizados ${r.actualizados} · omitidos ${r.omitidos}`); },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const activos = useMemo(() => comensales.filter((c) => c.activo).length, [comensales]);

  return (
    <div className="grid gap-4 p-4 lg:grid-cols-[280px_1fr]">
      {/* Contratos */}
      <Card className="h-fit">
        <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm"><Users className="h-4 w-4" /> Contratos</CardTitle></CardHeader>
        <CardContent className="space-y-1">
          {contratos.map((c) => (
            <button key={c.contrato_id} onClick={() => setContratoId(c.contrato_id)}
              className={`w-full rounded-md border p-2 text-left text-sm transition hover:border-primary ${cid === c.contrato_id ? 'border-primary ring-1 ring-primary' : ''}`}>
              <div className="truncate font-medium">{c.nombre}</div>
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className={`rounded-full px-1.5 ${c.modo_servicio === 'comensal' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' : 'bg-muted'}`}>{c.modo_servicio}</span>
                <span>{c.comensales} comensales</span>
              </div>
            </button>
          ))}
          {!contratos.length && <p className="text-sm text-muted-foreground">Sin contratos activos.</p>}
        </CardContent>
      </Card>

      {/* Padrón */}
      <div className="space-y-4">
        {contrato && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                {contrato.nombre}
                <span className={`rounded-full px-2 py-0.5 text-[11px] ${contrato.modo_servicio === 'comensal' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' : 'bg-muted text-muted-foreground'}`}>modo {contrato.modo_servicio}</span>
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={modo.isPending}
                  onClick={() => modo.mutate(contrato.modo_servicio === 'comensal' ? 'bloque' : 'comensal')}>
                  cambiar a {contrato.modo_servicio === 'comensal' ? 'bloque' : 'comensal'}
                </Button>
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {contrato.modo_servicio === 'comensal'
                  ? 'Modo por persona: cada comensal tendrá su menú y consumo (Fases 1-3).'
                  : 'Modo por bloque (actual): se produce y factura por pax. El padrón queda listo para activar el modo por persona.'}
              </p>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input className={`${fieldCls} w-56 pl-7`} placeholder="Buscar nombre o código" value={busca} onChange={(e) => setBusca(e.target.value)} />
                </div>
                <Button size="sm" className="gap-1" onClick={() => setEditor(nuevoEditor())}><UserPlus className="h-4 w-4" /> Nuevo</Button>
                <Button size="sm" variant="outline" className="gap-1" onClick={() => setImportar('')}><Upload className="h-4 w-4" /> Importar</Button>
                <span className="text-xs text-muted-foreground">{activos} activo(s)</span>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading && <p className="py-4 text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Cargando…</p>}
              {!isLoading && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="py-1 pr-2">Código</th><th className="py-1 pr-2">Nombre</th><th className="py-1 pr-2">Restricciones</th>
                      <th className="py-1 pr-2">Dieta habitual</th><th className="py-1 pr-2"></th>
                    </tr></thead>
                    <tbody>
                      {comensales.map((c) => (
                        <tr key={c.id} className={`border-b last:border-0 ${!c.activo ? 'opacity-50' : ''}`}>
                          <td className="py-1.5 pr-2 font-mono text-xs">{c.codigo ?? '—'}</td>
                          <td className="py-1.5 pr-2 font-medium">{c.nombre}{c.partner && <span className="block text-[11px] text-muted-foreground">{c.partner}</span>}</td>
                          <td className="py-1.5 pr-2">
                            <div className="flex flex-wrap gap-1">
                              {c.restricciones.map((r) => <span key={r} className="rounded-full bg-amber-100 px-1.5 text-[11px] text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">{r}</span>)}
                              {!c.restricciones.length && <span className="text-xs text-muted-foreground">—</span>}
                            </div>
                          </td>
                          <td className="py-1.5 pr-2 text-xs">{c.dieta ?? '—'}</td>
                          <td className="py-1.5 pr-2 text-right">
                            <button className="mr-2 text-muted-foreground hover:text-foreground" title="Editar"
                              onClick={() => setEditor({ id: c.id, codigo: c.codigo ?? '', nombre: c.nombre, restricciones: c.restricciones.join(', '), dieta_tipo_id: c.dieta_tipo_id ?? '', notas: c.notas ?? '', activo: c.activo })}><Pencil className="h-4 w-4" /></button>
                            <button className="text-muted-foreground hover:text-red-600" title="Dar de baja" onClick={() => baja.mutate(c.id)}><Trash2 className="h-4 w-4" /></button>
                          </td>
                        </tr>
                      ))}
                      {!comensales.length && <tr><td colSpan={5} className="py-3 text-sm text-muted-foreground">Sin comensales. Usa «Nuevo» o «Importar».</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Editor */}
        {editor && (
          <Card className="border-primary">
            <CardHeader className="pb-2"><CardTitle className="text-sm">{editor.id ? 'Editar comensal' : 'Nuevo comensal'}</CardTitle></CardHeader>
            <CardContent className="grid gap-2 sm:grid-cols-2">
              <div><label className="block text-xs text-muted-foreground">Código (badge/QR/habitación)</label><input className={`${fieldCls} w-full`} value={editor.codigo} onChange={(e) => setEditor({ ...editor, codigo: e.target.value })} /></div>
              <div><label className="block text-xs text-muted-foreground">Nombre *</label><input className={`${fieldCls} w-full`} value={editor.nombre} onChange={(e) => setEditor({ ...editor, nombre: e.target.value })} /></div>
              <div><label className="block text-xs text-muted-foreground">Restricciones (separadas por coma)</label><input className={`${fieldCls} w-full`} placeholder="maní, mariscos" value={editor.restricciones} onChange={(e) => setEditor({ ...editor, restricciones: e.target.value })} /></div>
              <div><label className="block text-xs text-muted-foreground">Dieta habitual</label>
                <select className={`${fieldCls} w-full`} value={editor.dieta_tipo_id} onChange={(e) => setEditor({ ...editor, dieta_tipo_id: e.target.value })}>
                  <option value="">— ninguna —</option>
                  {dietas.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2"><label className="block text-xs text-muted-foreground">Notas</label><input className={`${fieldCls} w-full`} value={editor.notas} onChange={(e) => setEditor({ ...editor, notas: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editor.activo} onChange={(e) => setEditor({ ...editor, activo: e.target.checked })} /> Activo</label>
              <div className="flex justify-end gap-2 sm:col-span-2">
                <Button size="sm" className="gap-1" disabled={guardar.isPending || !editor.nombre.trim()} onClick={() => guardar.mutate(editor)}><Check className="h-4 w-4" /> Guardar</Button>
                <Button size="sm" variant="outline" className="gap-1" onClick={() => setEditor(null)}><X className="h-4 w-4" /> Cancelar</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Importación */}
        {importar !== null && (
          <Card className="border-primary">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Importar comensales</CardTitle>
              <p className="text-xs text-muted-foreground">Una línea por comensal: <code>código, nombre, restricciones</code> (restricciones separadas por «;»). El código repetido actualiza en vez de duplicar.</p>
            </CardHeader>
            <CardContent className="space-y-2">
              <textarea className={`${fieldCls} h-40 w-full font-mono text-xs`} placeholder={'B001, Juan Pérez, maní; mariscos\nB002, Ana Gómez,'} value={importar} onChange={(e) => setImportar(e.target.value)} />
              <div className="flex justify-end gap-2">
                <Button size="sm" className="gap-1" disabled={importarMut.isPending || !importar.trim()} onClick={() => importarMut.mutate(importar)}><Upload className="h-4 w-4" /> Importar</Button>
                <Button size="sm" variant="outline" className="gap-1" onClick={() => setImportar(null)}><X className="h-4 w-4" /> Cancelar</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
